import 'firebase/compat/storage';
import { useEffect, useRef, useState } from 'react';
import { firebaseApp } from '../pages/login/firebaseInit';

/**
 * Resolves `gs://` photo references to a download URL plus the bits of metadata
 * the app cares about.
 *
 * The GPS of a photo is not on the GraphQL contract anywhere — it is written by
 * the PemPem app into the storage object's custom metadata as `photo_location`,
 * a JSON string holding a GeoJSON-ordered `coordinates` pair. Reading it here is
 * the same trick ContractDetail and ContractTraceableDetail already use.
 */

export type PhotoMeta = {
    url?: string
    /** `[lng, lat]`, from the object's `photo_location` custom metadata. */
    coordinates?: [number, number]
    /** When the object landed in the bucket, which is when the photo was taken. */
    capturedAt?: string
    failed?: boolean
}

const isValidCoordinate = (coords: any): coords is [number, number] =>
    Array.isArray(coords) && coords.length === 2 && coords.every(c => Number.isFinite(Number(c)));

const readCoordinates = (customMetadata: any): [number, number] | undefined => {
    const raw = customMetadata?.photo_location;
    if (!raw) return undefined;
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const coords = parsed?.coordinates;
        return isValidCoordinate(coords) ? [Number(coords[0]), Number(coords[1])] : undefined;
    } catch {
        // The field is free-form and has held non-JSON before; a missing
        // position is not worth failing the row over.
        return undefined;
    }
};

const resolve = async (uri: string): Promise<PhotoMeta> => {
    const ref = firebaseApp.storage().refFromURL(uri);
    const [metadata, url] = await Promise.all([ref.getMetadata(), ref.getDownloadURL()]);
    return {
        url,
        coordinates: readCoordinates(metadata?.customMetadata),
        capturedAt: metadata?.timeCreated,
    };
};

/**
 * Resolves each URI once per session. The cache is a ref rather than state so
 * that paging between market days never re-fetches an object already seen.
 */
export const useFirebasePhotos = (uris: string[]): Map<string, PhotoMeta> => {
    const cache = useRef<Map<string, PhotoMeta>>(new Map());
    const pending = useRef<Set<string>>(new Set());
    const [, setVersion] = useState(0);

    const key = uris.join('|');

    useEffect(() => {
        let cancelled = false;
        const missing = uris.filter(uri => uri && !cache.current.has(uri) && !pending.current.has(uri));
        if (missing.length === 0) return;

        missing.forEach(uri => pending.current.add(uri));

        Promise.all(missing.map(uri =>
            resolve(uri)
                .then(meta => cache.current.set(uri, meta))
                .catch(() => cache.current.set(uri, { failed: true }))
                .finally(() => pending.current.delete(uri))
        )).then(() => {
            if (!cancelled) setVersion(v => v + 1);
        });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    return cache.current;
};
